import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "../helper/HwIcon";
import { phoneCodeImageFn } from "../helper/commonFunction";

const sameOption = (a, b) =>
  a && b && a.value === b.value && a.country_code === b.country_code;

const HwPhoneCodeSelect = ({
  value,
  options = [],
  placeholder = "Phone Code*",
  onChange,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const [listMaxH, setListMaxH] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const positionMenu = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 6;
    const margin = 8;
    const w = Math.min(r.width, vw - 2 * margin);
    let left = r.left;
    if (left + w > vw - margin) left = vw - margin - w;
    if (left < margin) left = margin;

    const spaceBelow = vh - r.bottom - gap - margin;
    const spaceAbove = r.top - gap - margin;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const avail = Math.max(openUp ? spaceAbove : spaceBelow, 120);
    const maxH = Math.min(360, avail);

    const base = { position: "fixed", left, width: w, zIndex: 9999 };
    if (openUp) {
      // anchor above the field, but never above the top edge
      const bottom = Math.max(margin, vh - r.top + gap);
      setMenuStyle({ ...base, top: "auto", bottom });
    } else {
      // anchor below the field, but clamp so the whole menu stays on-screen
      const top = Math.max(margin, Math.min(r.bottom + gap, vh - maxH - margin));
      setMenuStyle({ ...base, top, bottom: "auto" });
    }
    setListMaxH(maxH);
  };

  const openMenu = () => {
    positionMenu();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) {
      setQuery("");
      setMenuStyle(null);
      return;
    }
    positionMenu();
    const raf = requestAnimationFrame(positionMenu);
    const onDoc = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const reposition = () => positionMenu();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    const t = setTimeout(
      () => inputRef.current && inputRef.current.focus({ preventScroll: true }),
      0
    );
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      clearTimeout(t);
      cancelAnimationFrame(raf);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        String(o.label).toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q)
    );
  }, [query, options]);

  const showFlag = value?.country_code && !open;

  const menu =
    open && menuStyle
      ? createPortal(
          <div ref={menuRef} className="hw-pcsel--menu" style={menuStyle}>
            <ul
              className="hw-pcsel--list"
              role="listbox"
              style={{ maxHeight: listMaxH ? `${listMaxH}px` : undefined }}
            >
              {filtered.length ? (
                filtered.map((o) => (
                  <li
                    key={`${o.value}-${o.country_code}`}
                    role="option"
                    aria-selected={sameOption(value, o)}
                    className={`hw-pcsel--option ${
                      sameOption(value, o) ? "is-selected" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(o);
                      setOpen(false);
                    }}
                  >
                    <img
                      className="hw-pcsel--flag"
                      alt=""
                      src={phoneCodeImageFn(o.country_code)}
                      width={20}
                      height={20}
                    />
                    <span>{o.label}</span>
                  </li>
                ))
              ) : (
                <li className="hw-pcsel--empty">No results</li>
              )}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={ref}
      className={`hw-pcsel ${open ? "is-open" : ""} ${error ? "is-error" : ""}`}
    >
      <div
        className="hw-pcsel--control"
        onMouseDown={() => {
          if (!open) openMenu();
        }}
      >
        {showFlag ? (
          <img
            className="hw-pcsel--flag"
            alt=""
            src={phoneCodeImageFn(value.country_code)}
            width={20}
            height={20}
          />
        ) : null}
        <input
          ref={inputRef}
          className="hw-pcsel--input"
          type="text"
          value={open ? query : value?.label ?? ""}
          placeholder={value ? value.label : placeholder}
          readOnly={!open}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered.length) {
                onChange(filtered[0]);
                setOpen(false);
              }
            }
          }}
        />
        <span
          className="hw-pcsel--chevron"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (open) {
              setOpen(false);
            } else {
              openMenu();
            }
          }}
        >
          <ChevronDownIcon />
        </span>
      </div>
      {menu}
    </div>
  );
};

export default HwPhoneCodeSelect;
